import React from 'react';
import { BackgroundImageSettings } from '@loyalty/shared-common/template-api-client';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { UploadableImage } from '../../../../shared/image';
import { useProgram } from '../../../../loyalty/ProgramContext';

const SIZE_OPTIONS = [
  { value: 'auto', text: 'Auto' },
  { value: 'cover', text: 'Cover' },
  { value: 'contain', text: 'Contain' },
  { value: '100% 100%', text: 'Stretch' },
];

interface BackgroundImageFieldProps {
  name: string;
  label?: string;
  onApply?: () => void;
}

export function BackgroundImageField({
  name,
  label,
  onApply,
}: BackgroundImageFieldProps): React.ReactElement {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    watch,
  } = useFormContext();
  const { program } = useProgram();

  const fieldValue = watch(name) as BackgroundImageSettings | undefined;
  const imageUrl = fieldValue?.url || '';

  const handleError = (fieldName: string, message?: string) => {
    if (message) {
      setError(fieldName, { message });
    } else {
      clearErrors(fieldName);
    }
  };

  const handleImageChange = (onChange: (value: any) => void, value: string) => {
    onChange({
      ...fieldValue,
      url: value,
      repeat: fieldValue?.repeat ?? false,
      position: 'center',
      size: fieldValue?.size ?? 'cover',
    });
    clearErrors(name);
    onApply?.();
  };

  const handleRemoveImage = (onChange: (value: any) => void) => {
    onChange(undefined);
    clearErrors(name);
    onApply?.();
  };

  const handlePropertyChange = (
    onChange: (value: any) => void,
    property: keyof BackgroundImageSettings,
    value: string | boolean,
  ) => {
    onChange({
      ...fieldValue,
      position: 'center',
      [property]: value,
    });
    onApply?.();
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
        const bgImageValue = (value as BackgroundImageSettings) || {};
        return (
          <FormControl style={{ marginBottom: '5px' }} error={!!error} fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <FormLabel>{label}</FormLabel>
              {imageUrl && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(onChange)}
                  sx={{
                    color: colorPalette.red.main,
                    '&:hover': {
                      backgroundColor: colorPalette.red.light,
                      color: colorPalette.red.dark,
                    },
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </IconButton>
              )}
            </Box>
            <UploadableImage
              {...field}
              value={imageUrl}
              programId={program.id}
              type={name}
              errorMessage={undefined}
              size={175}
              onChange={event => {
                const newValue = (event.target as HTMLInputElement).value;
                handleImageChange(onChange, newValue);
              }}
              onError={handleError}
            />
            {error?.message && (
              <FormHelperText error>
                <span>{error.message}</span>
              </FormHelperText>
            )}
            {imageUrl && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  select
                  label="Size"
                  value={bgImageValue.size || 'cover'}
                  onChange={e => handlePropertyChange(onChange, 'size', e.target.value as string)}
                  fullWidth
                  size="small"
                >
                  {SIZE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Repeat"
                  value={bgImageValue.repeat ? 'yes' : 'no'}
                  onChange={e => handlePropertyChange(onChange, 'repeat', e.target.value === 'yes')}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="no">No Repeat</MenuItem>
                  <MenuItem value="yes">Repeat</MenuItem>
                </TextField>
              </Box>
            )}
          </FormControl>
        );
      }}
    />
  );
}
